// public/v3/src/lib/live-client.js — the browser side of the GPT-Live control
// socket (server/live/core/frames.js, protocol v1). Framework-free on purpose
// (design record §"Portability"): no React, no ccm API client — the socket URL
// and a token provider are injected, and the page only wires the events.
//
//   mic (getUserMedia) → RTCPeerConnection (+ the negotiated 'oai-events' data
//   channel, id 4, which carries nothing by default) → offer → ICE gathering
//   (bounded) → our socket: hello → ready → start{sdp, pinnedPatient} → started
//   → setRemoteDescription(answer) → remote track → <audio autoplay>.
//
// Audio never rides the socket. Captions, thinking marks, the backend's verified
// replies and the Order Chat card payloads (`turn` frames, the /send body) do.
//
// State machine: idle → connecting → live (phase listening | speaking | thinking
// | reconnecting) → ended. A failure before `started` goes back to idle (there is
// nothing to end); a `closed` frame or a fatal failure while live goes to ended.
//
// Socket close codes (frames.js): 4401 → fetch a fresh token ONCE and resume;
// 4403 → the feature is off for this practice; 4400 / 1011 → our fault; anything
// else while live → reconnect and `resume {sessionId}` inside the server's grace.
//
// Every browser global is injectable through `deps` so the whole thing runs
// under node with fakes (tests/unit/v3/live-client.test.js).

const STATES = Object.freeze(['idle', 'connecting', 'live', 'ended']);
const PHASES = Object.freeze(['listening', 'speaking', 'thinking', 'reconnecting']);

// Plain sentences per error code. The server's own `userMessage` wins when a
// frame carries one; these cover the client-side codes and a frame without text.
const ERROR_MESSAGES = Object.freeze({
  busy: 'Live voice is busy right now — try again in a minute.',
  not_configured: 'Live voice is not set up on the server.',
  unavailable: 'Live voice is unavailable right now. Try again in a moment.',
  audio_connection: 'The audio connection was refused. Check your microphone and try again.',
  auth_lost: 'Your session expired. Sign in again to keep talking.',
  backend_failed: 'The assistant could not reach its backend and ended the call.',
  tool_failed: 'One step failed; the assistant will tell you what happened.',
  server_error: 'Live voice hit a problem on our side. Try again.',
  malformed: 'Live voice hit a problem on our side. Try again.',
  already_started: 'A live call is already running.',
  no_session: 'That live call is no longer running.',
  mic_denied: 'Microphone access was refused. Allow the microphone for this site and try again.',
  mic_unavailable: 'No microphone was found.',
  feature_off: 'Live voice is turned off for this practice.',
  connection_lost: 'The connection to the server dropped and could not be resumed.',
  unsupported: 'This browser cannot run live voice (WebRTC is missing).',
});
const GENERIC_MESSAGE = 'Live voice hit a problem. Try again.';

function errorMessage(code, userMessage) {
  if (typeof userMessage === 'string' && userMessage.trim()) return userMessage;
  return ERROR_MESSAGES[code] || GENERIC_MESSAGE;
}

// ws(s)://<this host><socketPath> — the server's /api/live/config names the path.
function socketUrlFor(socketPath, loc) {
  const l = loc || (typeof window !== 'undefined' ? window.location : null);
  if (!l) throw new Error('socketUrlFor: a location is required');
  const proto = l.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${l.host}${socketPath}`;
}

// The caption rail: deltas stitched per speaker, the last `max` entries kept.
// Returns a NEW array (the page keeps it in React state).
function stitchCaption(entries, frame, max = 12) {
  const list = Array.isArray(entries) ? entries.slice() : [];
  const delta = typeof frame.delta === 'string' ? frame.delta : '';
  if (!delta) return list;
  const speaker = frame.speaker === 'assistant' ? 'assistant' : 'user';
  const last = list[list.length - 1];
  if (last && last.speaker === speaker) {
    list[list.length - 1] = { ...last, text: last.text + delta, endMs: frame.end_ms ?? last.endMs };
  } else {
    list.push({ speaker, text: delta, startMs: frame.start_ms ?? null, endMs: frame.end_ms ?? null, time: Date.now() });
  }
  return list.length > max ? list.slice(list.length - max) : list;
}

// `reply` frames are the backend's verified sentence for one delegation. When
// that delegation also produced a card (`turn` frame), the card already says it
// and the voice already said it — a bubble would be a third copy. The page keeps
// {delegationId → the turn's reply lines} for the call and asks this before it
// draws a bubble, a beat after the reply (the turn can land a moment later).
function replyCoveredByTurn(turnReplies, delegationId, text) {
  if (!turnReplies || typeof turnReplies.get !== 'function') return false;
  if (delegationId != null && turnReplies.has(delegationId)) return true;
  const norm = (t) => String(t || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const want = norm(text);
  if (!want) return true;
  for (const lines of turnReplies.values()) {
    if (Array.isArray(lines) && lines.some((l) => norm(l) === want)) return true;
  }
  return false;
}

function defaultAudioElement() {
  const a = document.createElement('audio');
  a.autoplay = true;
  a.setAttribute('playsinline', '');
  a.style.display = 'none';
  document.body.appendChild(a);
  return a;
}

function micErrorCode(err) {
  const name = err && err.name;
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') return 'mic_unavailable';
  return 'mic_denied';
}

function closeCodeToError(code) {
  if (code === 4401) return 'auth_lost';
  if (code === 4403) return 'feature_off';
  if (code === 4400 || code === 1011) return 'server_error';
  return 'connection_lost';
}

class LiveClient {
  constructor({
    socketUrl, getToken, onFrame, onState, onError,
    iceTimeoutMs = 3000, pingMs = 20000, resumeGraceMs = 12000, speakingHoldMs = 1500, endTimeoutMs = 5000, handshakeTimeoutMs = 15000,
    deps = {},
  } = {}) {
    if (typeof socketUrl !== 'string' || !socketUrl) throw new Error('LiveClient: socketUrl required');
    if (typeof getToken !== 'function') throw new Error('LiveClient: getToken required');
    this.socketUrl = socketUrl;
    this.getToken = getToken;
    this.onFrame = typeof onFrame === 'function' ? onFrame : () => {};
    this.onState = typeof onState === 'function' ? onState : () => {};
    this.onError = typeof onError === 'function' ? onError : () => {};
    this.iceTimeoutMs = iceTimeoutMs;
    this.pingMs = pingMs;
    this.resumeGraceMs = resumeGraceMs;
    this.speakingHoldMs = speakingHoldMs;
    this.endTimeoutMs = endTimeoutMs;
    this.handshakeTimeoutMs = handshakeTimeoutMs; // ready / started must arrive within this, or the start fails (mic released)
    const g = typeof globalThis !== 'undefined' ? globalThis : {};
    this.deps = {
      WebSocket: deps.WebSocket || g.WebSocket,
      RTCPeerConnection: deps.RTCPeerConnection || g.RTCPeerConnection,
      getUserMedia: deps.getUserMedia || ((c) => g.navigator.mediaDevices.getUserMedia(c)),
      createAudioElement: deps.createAudioElement || defaultAudioElement,
      setTimeout: deps.setTimeout || ((fn, ms) => setTimeout(fn, ms)),
      clearTimeout: deps.clearTimeout || ((t) => clearTimeout(t)),
      now: deps.now || (() => Date.now()),
    };
    this.state = 'idle';
    this.phase = null;
    this.sessionId = null;
    this.ready = null; // the last `ready` frame (caps, captionsVia, limits)
    this._gen = 0;
    this._ws = null;
    this._pc = null;
    this._dc = null;
    this._stream = null;
    this._audio = null;
    this._pending = new Map(); // frame type → { resolve, reject }
    this._timers = {};
    this._retriedAuth = false;
    this._destroyed = false;
    this._startSent = false;     // the `start` frame went out: the server may run a session for us
    this._resumeSent = false;    // a `resume` went out on the current socket (the server binds the session behind it)
    this._sessionOnSocket = false; // `started` / `resumed` arrived on the current socket: `end` reaches a session
    this._wantPin = undefined;   // the page's latest pin (string id or null); undefined = never set
    this._sentPin = undefined;   // the pin the server last heard from us
    this.engine = null;          // from `started` (informational: 'live' | 'realtime')
    this._lastSeq = 0;           // highest `seq` received on a result frame (the resume cursor)
  }

  // ── public ────────────────────────────────────────────────────────────────

  async start({ pinnedPatient } = {}) {
    if (this._destroyed) throw new Error('LiveClient: destroyed');
    if (this.state === 'connecting' || this.state === 'live') return;
    const gen = ++this._gen;
    this.sessionId = null;
    this._retriedAuth = false;
    this._startSent = false;
    this._resumeSent = false;
    this._sessionOnSocket = false;
    this.engine = null;
    this.ready = null;
    this._lastSeq = 0;
    this._ending = null;         // set by End (or a fatal error while live): a drop after it is terminal, never a reconnect
    this._reconnecting = null;
    this._wantPin = pinnedPatient && pinnedPatient.directoryId != null ? String(pinnedPatient.directoryId) : null;
    this._sentPin = undefined;
    this._setState('connecting', null);
    const D = this.deps;
    if (typeof D.RTCPeerConnection !== 'function' || typeof D.WebSocket !== 'function') {
      return this._fail('unsupported');
    }
    let stream;
    try {
      stream = await D.getUserMedia({ audio: true });
    } catch (err) {
      // End/destroy while the permission prompt was up: there is nothing to report.
      if (gen !== this._gen) return;
      return this._fail(micErrorCode(err));
    }
    if (gen !== this._gen) { stopTracks(stream); return; }
    this._stream = stream;
    let pc;
    try {
      pc = new D.RTCPeerConnection();
      this._pc = pc;
      for (const track of stream.getTracks()) pc.addTrack(track, stream);
      // Negotiated so no SDP round trip is spent on it; the vendor sends nothing
      // over it unless asked (captions ride our socket — `captionsVia: 'socket'`).
      this._dc = pc.createDataChannel('oai-events', { negotiated: true, id: 4 });
      pc.ontrack = (ev) => this._attachRemote(ev);
      // Media health is watched separately from the control socket: a peer
      // connection that fails (or closes) ends the call with audio_connection —
      // never a "listening" pill over a dead transport (codex review #797).
      pc.onconnectionstatechange = () => { if (gen === this._gen) this._onMediaState(pc.connectionState); };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await this._waitIce(pc);
      if (gen !== this._gen) return;
      const sdp = (pc.localDescription && pc.localDescription.sdp) || offer.sdp;
      let token = await this.getToken({ fresh: false });
      if (gen !== this._gen) return;
      try {
        await this._openSocket(token, { timeoutMs: this.handshakeTimeoutMs });
      } catch (err) {
        // 4401 at hello on a cached token (revoked by a re-login in another tab,
        // or lapsed): one fresh token and one more try, then the failure stands.
        if (!(err && err.closeCode === 4401) || this._retriedAuth || gen !== this._gen) throw err;
        this._retriedAuth = true;
        token = await this.getToken({ fresh: true });
        if (gen !== this._gen) return;
        await this._openSocket(token, { timeoutMs: this.handshakeTimeoutMs });
      }
      if (gen !== this._gen) return;
      // The pin the frame carries is the page's LATEST (it may have changed
      // while the mic prompt was up); `_sentPin` records what the server heard.
      const startFrame = { type: 'start', sdp };
      const pinId = this._wantPin == null ? null : this._wantPin;
      if (pinId != null) startFrame.pinnedPatient = { directoryId: pinId };
      // The `started` waiter is registered BEFORE the frame goes out, so a
      // socket closing in that gap rejects it instead of hanging the handshake.
      const startedP = this._expect('started', this.handshakeTimeoutMs);
      if (!this._send(startFrame)) throw Object.assign(new Error('socket closed'), { code: 'connection_lost' });
      this._startSent = true;
      this._sentPin = pinId;
      const started = await startedP;
      if (gen !== this._gen) return;
      this._startSent = false;       // answered: from here `_sessionOnSocket` says whether `end` has a session to reach
      this._sessionOnSocket = true;
      this.sessionId = started.sessionId;
      this.engine = typeof started.engine === 'string' ? started.engine : null;
      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: started.sdp });
      } catch (err) {
        if (gen !== this._gen) return;
        return this._fail('audio_connection'); // _fail tells the server `end`: it runs a session for us
      }
      if (gen !== this._gen) return;
      // A close that landed during setRemoteDescription has no waiter to reject:
      // never go 'live' on a dead socket — nor on a media transport already dead.
      if (!this._ws || this._ws.readyState !== 1) return this._fail('connection_lost');
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') return this._fail('audio_connection');
      this._startPing();
      this._setState('live', 'listening');
      this._syncPin(); // a pin changed while connecting reaches the server now
    } catch (err) {
      if (gen !== this._gen) return;
      return this._fail(failureCode(err), err && err.userMessage);
    }
  }

  // Forward the page's pin. Remembered always; sent now when the socket is up,
  // else on the next `started` / `resumed` — a pin changed while connecting or
  // reconnecting must not leave the server acting on the old patient.
  pin(directoryId) {
    this._wantPin = directoryId == null ? null : String(directoryId);
    if (this.state === 'idle' || this.state === 'ended') return false;
    return this._syncPin();
  }

  _syncPin() {
    if (this._wantPin === undefined || this._wantPin === this._sentPin) return false;
    // Only to a socket the server has bound the session on: a reconnect socket
    // that has said hello but not yet `resumed` would answer no_session, and
    // the pin — recorded as sent — would never reach the resumed session
    // (codex review r2).
    if (!this.sessionId || !this._sessionOnSocket || !this._ws || this._ws.readyState !== 1) return false;
    if (!this._send({ type: 'pin', directoryId: this._wantPin })) return false;
    this._sentPin = this._wantPin;
    return true;
  }

  // Tell the server to end the call, when there is a socket to say it on. Sent
  // for a live call and for a connect that already sent `start` (the server
  // cancels a start in flight or closes the session): a vendor session must
  // never outlive the tab. Returns whether it went out.
  _sayEnd() {
    // Only to a socket the server binds a session on: after `start` went out
    // (the server may be creating one), after `resume` went out (`end` queues
    // behind it), or once `started` / `resumed` arrived. A reconnect socket that
    // has only said hello would answer `no_session`; a start the server refused
    // with a fatal error has no session to end.
    if (!this._startSent && !this._resumeSent && !this._sessionOnSocket) return false;
    return this._send({ type: 'end' });
  }

  // Ask the server to close; `closed` finishes the call. The mic stops at once.
  end() {
    if (this.state === 'connecting') {
      this._gen++;
      this._sayEnd();
      this._finish('client_end', {});
      return;
    }
    if (this.state !== 'live') return;
    this._ending = 'client_end';
    stopTracks(this._stream);
    if (!this._sayEnd()) { this._finish('client_end', {}); return; } // no socket to hear it: nothing to wait for
    this._clear('end');
    this._timers.end = this.deps.setTimeout(() => { if (this.state === 'live') this._finish('client_end', {}); }, this.endTimeoutMs);
  }

  // Re-attach the control channel to a session whose socket dropped (the
  // automatic path after a close, and a page that still holds a sessionId).
  async resume(sessionId) {
    if (this._destroyed) throw new Error('LiveClient: destroyed');
    if (this.state === 'connecting') return false;
    if (sessionId) this.sessionId = sessionId;
    if (!this.sessionId) return false;
    if (this.state !== 'live') this.state = 'live';
    return this._reconnect({ freshToken: false });
  }

  // Unmount / unload: tell the server (so billing stops at once), then drop
  // everything. Silent by default (an unmounting page wants no callback);
  // `notify` reports `ended` — a page leaving for the back/forward cache must
  // not come back showing a live pill for a call that ended when it left.
  destroy({ notify = false } = {}) {
    this._destroyed = true;
    this._gen++;
    const wasActive = this.state === 'connecting' || this.state === 'live';
    this._sayEnd();
    this._rejectPending(Object.assign(new Error('destroyed'), { code: 'client_end' }));
    this._teardown();
    this.state = 'ended';
    this.phase = null;
    if (notify && wasActive) this.onState({ state: 'ended', phase: null, sessionId: this.sessionId, reason: 'client_end', seconds: null });
  }

  // ── socket ────────────────────────────────────────────────────────────────

  _openSocket(token, { timeoutMs } = {}) {
    const D = this.deps;
    const ws = new D.WebSocket(this.socketUrl);
    this._ws = ws;
    const ready = this._expect('ready', timeoutMs);
    // `seq`: result frames come numbered and a resume reports the last one seen,
    // so a replay never repeats what landed and never loses what did not.
    ws.onopen = () => { if (this._ws === ws) this._send({ type: 'hello', token, client: 'web', caps: ['seq'] }); };
    ws.onmessage = (ev) => { if (this._ws === ws) this._onMessage(ev && ev.data); };
    ws.onclose = (ev) => { if (this._ws === ws) this._onClose(ev && typeof ev.code === 'number' ? ev.code : 1006); };
    ws.onerror = () => { /* a close follows */ };
    return ready.then((frame) => { this.ready = frame; return frame; });
  }

  _send(frame) {
    const ws = this._ws;
    if (!ws || ws.readyState !== 1) return false;
    try { ws.send(JSON.stringify(frame)); return true; } catch (_) { return false; }
  }

  // Wait for one frame type; with `timeoutMs`, give up with code 'timeout' (a
  // reconnect attempt whose socket neither opens nor closes must not hang).
  _expect(type, timeoutMs) {
    return new Promise((resolve, reject) => {
      const entry = { resolve, reject, timer: null };
      if (timeoutMs) {
        entry.timer = this.deps.setTimeout(() => {
          if (this._pending.get(type) !== entry) return;
          this._pending.delete(type);
          reject(Object.assign(new Error(`no ${type} in time`), { code: 'timeout' }));
        }, timeoutMs);
      }
      this._pending.set(type, entry);
    });
  }

  _rejectPending(err) {
    const waiters = [...this._pending.values()];
    this._pending.clear();
    for (const w of waiters) { if (w.timer) this.deps.clearTimeout(w.timer); w.reject(err); }
  }

  _onMessage(data) {
    let frame;
    try { frame = JSON.parse(typeof data === 'string' ? data : String(data)); } catch (_) { return; }
    if (!frame || typeof frame.type !== 'string') return;
    const waiter = this._pending.get(frame.type);
    if (waiter) { this._pending.delete(frame.type); if (waiter.timer) this.deps.clearTimeout(waiter.timer); waiter.resolve(frame); return; }
    if (Number.isInteger(frame.seq)) {
      if (frame.seq <= this._lastSeq) return; // a replay of what already landed
      this._lastSeq = frame.seq;
    }
    switch (frame.type) {
      case 'error': {
        const err = Object.assign(new Error(frame.code || 'error'), { code: frame.code || 'server_error', userMessage: frame.userMessage, fatal: !!frame.fatal });
        if (err.fatal && this._pending.size) this._startSent = false; // the server refused the start: no session to end
        if (this._pending.size) { this._rejectPending(err); return; } // the handshake in flight reports it
        this.onError({ code: err.code, message: errorMessage(err.code, frame.userMessage), fatal: err.fatal, frame });
        if (err.fatal && this.state === 'live') {
          // The server closes the session after a fatal error and says so with
          // `closed`; finish ourselves if that never comes — and never reconnect.
          this._ending = err.code;
          this._clear('end');
          this._timers.end = this.deps.setTimeout(() => { if (this.state === 'live') this._finish(err.code, {}); }, this.endTimeoutMs);
        }
        return;
      }
      case 'caption':
        this.onFrame(frame);
        if (frame.speaker === 'assistant') {
          this._setPhase('speaking');
          this._clear('speaking');
          this._timers.speaking = this.deps.setTimeout(() => { if (this.phase === 'speaking') this._setPhase(this._thinking ? 'thinking' : 'listening'); }, this.speakingHoldMs);
        } else if (this.phase === 'speaking') {
          this._clear('speaking');
          this._setPhase(this._thinking ? 'thinking' : 'listening');
        }
        return;
      case 'thinking':
        this.onFrame(frame);
        this._thinking = !!frame.active;
        if (this.phase !== 'speaking') this._setPhase(this._thinking ? 'thinking' : 'listening');
        return;
      case 'closed':
        this.onFrame(frame);
        this._finish(frame.reason || 'closed', { seconds: frame.seconds == null ? null : frame.seconds });
        return;
      case 'pong':
        this._pingsUnanswered = 0;
        return;
      default:
        this.onFrame(frame); // reply, turn, and anything newer (forward-compat)
    }
  }

  // The media transport failed or closed under a call: say `end` (the server
  // still runs a session) and end with audio_connection.
  _onMediaState(s) {
    if (s !== 'failed' && s !== 'closed') return;
    if (this.state === 'connecting') { this._fail('audio_connection'); return; }
    if (this.state !== 'live') return;
    this._sayEnd();
    this._lose('audio_connection', 'connection_lost');
  }

  _onClose(code) {
    this._ws = null;
    this._sentPin = undefined;     // nobody acknowledged it: the current pin is re-sent after `resumed` (codex review #797)
    this._startSent = false;       // a start in flight on that socket has no socket to be ended on
    this._resumeSent = false;
    this._sessionOnSocket = false; // whatever the server bound to that socket is gone with it
    this._clear('ping');
    if (this._pending.size) {
      // A handshake (hello/start/resume) in flight: its awaiter maps the code.
      this._rejectPending(Object.assign(new Error(`socket closed ${code}`), { closeCode: code, code: closeCodeToError(code) }));
      return;
    }
    // A close in a handshake gap with no waiter (after `ready`, before `start`
    // went out; during setRemoteDescription): fail the start, never hang it.
    if (this.state === 'connecting') { this._fail(closeCodeToError(code)); return; }
    if (this.state !== 'live') return;
    // End (or a fatal error) already decided how this call ends: a drop inside
    // the `closed` wait is the end, not a reason to reconnect (verification r2 R2-F4).
    if (this._ending) { this._finish(this._ending, {}); return; }
    if (code === 4403) return this._lose('feature_off', 'forbidden');
    if (code === 4400 || code === 1011) return this._lose('server_error', 'server_error');
    if (code === 4401) {
      if (this._retriedAuth) return this._lose('auth_lost', 'auth_lost');
      this._retriedAuth = true;
      this._reconnect({ freshToken: true });
      return;
    }
    this._reconnect({ freshToken: false });
  }

  // Reconnect + resume inside the server's client-drop grace; one fresh token on
  // a 4401 along the way, then give up.
  async _reconnect({ freshToken }) {
    if (this._reconnecting) return this._reconnecting;
    const gen = this._gen;
    const deadline = this.deps.now() + this.resumeGraceMs;
    this._setPhase('reconnecting');
    const run = async () => {
      let wantFresh = !!freshToken;
      for (;;) {
        if (gen !== this._gen || this.state !== 'live') return false;
        if (this._ending) { this._finish(this._ending, {}); return false; }
        try {
          // Each step is bounded by what is left of the grace (at most 5 s):
          // the token fetch (a stalled API must not hold 'Reconnecting…' past
          // the server's grace — codex review #797), the socket open, and the
          // resumed wait.
          const bound = () => Math.max(250, Math.min(5000, deadline - this.deps.now()));
          const token = await Promise.race([
            this.getToken({ fresh: wantFresh }),
            new Promise((_, reject) => { const t = this.deps.setTimeout(() => reject(Object.assign(new Error('no token in time'), { code: 'timeout' })), bound()); if (t && t.unref) t.unref(); }),
          ]);
          if (gen !== this._gen || this.state !== 'live') return false;
          await this._openSocket(token, { timeoutMs: bound() });
          if (gen !== this._gen || this.state !== 'live') return false;
          this._send({ type: 'resume', sessionId: this.sessionId, lastSeq: this._lastSeq });
          this._resumeSent = true;
          await this._expect('resumed', bound()); // re-measured: the open already spent part of this attempt
          if (gen !== this._gen || this.state !== 'live') return false;
          this._sessionOnSocket = true;
          this._startPing();
          this._setPhase(this._thinking ? 'thinking' : 'listening');
          this._syncPin(); // a pin changed during the outage reaches the server now
          return true;
        } catch (err) {
          if (gen !== this._gen || this.state !== 'live') return false;
          if (err && err.code === 'timeout') {
            // Drop the half-open socket so its late close cannot start a second reconnect.
            const ws = this._ws; this._ws = null;
            if (ws) { try { ws.onclose = null; ws.close(1000); } catch (_) { /* noop */ } }
          }
          const cc = err && err.closeCode;
          if (cc === 4403) { this._lose('feature_off', 'forbidden'); return false; }
          if (cc === 4401) {
            // One fresh token per session, then give up.
            if (wantFresh || this._retriedAuth) { this._lose('auth_lost', 'auth_lost'); return false; }
            this._retriedAuth = true; wantFresh = true; continue;
          }
          if (err && err.code === 'no_session') { this._lose('connection_lost', 'connection_lost'); return false; }
          if (this.deps.now() >= deadline) { this._lose('connection_lost', 'connection_lost'); return false; }
          await new Promise((r) => this.deps.setTimeout(r, 1000));
        }
      }
    };
    this._reconnecting = run().finally(() => { this._reconnecting = null; });
    return this._reconnecting;
  }

  // Pings are answered by pongs; two in a row unanswered mean a link that died
  // without a close (verification r2 R2-F12): the socket is dropped as if it
  // had closed abnormally, and the usual reconnect runs.
  _startPing() {
    this._clear('ping');
    this._pingsUnanswered = 0;
    const tick = () => {
      if (this.state !== 'live') return;
      if (this._pingsUnanswered >= 2) {
        const ws = this._ws;
        if (ws) { try { ws.onclose = null; ws.close(1000); } catch (_) { /* noop */ } }
        this._onClose(1006);
        return;
      }
      this._pingsUnanswered += 1;
      this._send({ type: 'ping' });
      this._timers.ping = this.deps.setTimeout(tick, this.pingMs);
    };
    this._timers.ping = this.deps.setTimeout(tick, this.pingMs);
  }

  // ── media ─────────────────────────────────────────────────────────────────

  _waitIce(pc) {
    return new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') return resolve();
      let done = false;
      let timer = null;
      const finish = () => {
        if (done) return;
        done = true;
        if (timer) this.deps.clearTimeout(timer);
        if (typeof pc.removeEventListener === 'function') pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      };
      const onChange = () => { if (pc.iceGatheringState === 'complete') finish(); };
      if (typeof pc.addEventListener === 'function') pc.addEventListener('icegatheringstatechange', onChange);
      // Past the bound, send the offer with the candidates gathered so far. The
      // timer is this wait's own (never in _timers): a start torn down
      // mid-gathering still settles here, and cannot clear a later start's bound.
      timer = this.deps.setTimeout(finish, this.iceTimeoutMs);
    });
  }

  _attachRemote(ev) {
    try {
      if (!this._audio) this._audio = this.deps.createAudioElement();
      const stream = ev && ev.streams && ev.streams[0]
        ? ev.streams[0]
        : (typeof globalThis.MediaStream === 'function' && ev && ev.track ? new globalThis.MediaStream([ev.track]) : null);
      if (stream) this._audio.srcObject = stream;
      const p = typeof this._audio.play === 'function' ? this._audio.play() : null;
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) { /* audio element is best effort; the call continues */ }
  }

  _teardown() {
    for (const k of Object.keys(this._timers)) this._clear(k);
    this._reconnecting = null;   // a stale in-flight guard must not block the next call on a reused instance (R2-F13)
    this._thinking = false;
    this._startSent = false;
    this._resumeSent = false;
    this._sessionOnSocket = false;
    const ws = this._ws;
    this._ws = null;
    if (ws) { try { ws.onclose = null; ws.close(1000); } catch (_) { /* already closed */ } }
    stopTracks(this._stream);
    this._stream = null;
    if (this._dc) { try { this._dc.close(); } catch (_) { /* noop */ } this._dc = null; }
    if (this._pc) { try { this._pc.close(); } catch (_) { /* noop */ } this._pc = null; }
    if (this._audio) {
      try { this._audio.srcObject = null; } catch (_) { /* noop */ }
      try { if (this._audio.remove) this._audio.remove(); } catch (_) { /* noop */ }
      this._audio = null;
    }
  }

  // ── state ─────────────────────────────────────────────────────────────────

  _setState(state, phase, extra) {
    this.state = state;
    this.phase = phase;
    this.onState({ state, phase, sessionId: this.sessionId, ...(extra || {}) });
  }

  _setPhase(phase) {
    if (this.state !== 'live' || this.phase === phase) return;
    this.phase = phase;
    this.onState({ state: 'live', phase, sessionId: this.sessionId });
  }

  // Before the call is live: back to idle, and say why. After `start` went out
  // the server may already run a session for us — tell it first.
  _fail(code, userMessage) {
    this._gen++;
    this._sayEnd();
    this._rejectPending(Object.assign(new Error(code), { code }));
    this._teardown();
    this.sessionId = null;
    this._setState('idle', null, { reason: code });
    this.onError({ code, message: errorMessage(code, userMessage), fatal: true });
  }

  // While live, without a `closed` frame: the error, then the end.
  _lose(code, reason) {
    this.onError({ code, message: errorMessage(code), fatal: true });
    this._finish(reason, {});
  }

  _finish(reason, { seconds } = {}) {
    if (this.state === 'ended') return;
    this._gen++;
    this._rejectPending(Object.assign(new Error('ended'), { code: 'client_end' }));
    this._teardown();
    this._setState('ended', null, { reason, seconds: seconds == null ? null : seconds });
  }

  _clear(name) {
    if (this._timers[name]) { this.deps.clearTimeout(this._timers[name]); this._timers[name] = null; }
  }
}

function stopTracks(stream) {
  if (!stream || typeof stream.getTracks !== 'function') return;
  for (const t of stream.getTracks()) { try { t.stop(); } catch (_) { /* noop */ } }
}

// An error thrown inside start(): a refused handshake (error frame → its code), a
// socket closed during it (→ its close code's meaning), or anything else.
function failureCode(err) {
  if (!err) return 'server_error';
  if (typeof err.closeCode === 'number') return closeCodeToError(err.closeCode);
  if (err.code === 'timeout') return 'connection_lost'; // a handshake that stopped progressing
  if (typeof err.code === 'string' && err.code) return err.code;
  // GET /api/live/token through the page's API client: a 404 (the flag went
  // off), a 403 (no permission) or a 401 (signed out) is not "our fault".
  if (err.status === 404 || err.status === 403) return 'feature_off';
  if (err.status === 401 || err.message === 'unauthenticated') return 'auth_lost';
  return 'server_error';
}

module.exports = { LiveClient, ERROR_MESSAGES, errorMessage, socketUrlFor, stitchCaption, replyCoveredByTurn, STATES, PHASES };
